import { useState } from "react";

import { Box, Typography } from "@mui/material";

import { ListNumbersIcon } from "@phosphor-icons/react/dist/ssr";
import { doc, updateDoc } from "firebase/firestore";

import { TabHeader } from "@/components/Luna/features";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { LessonStep } from "@/types";
import { db } from "@/utils/firebase";

import { LessonPlanItem, LessonPlanSkeleton } from "./index";

const LessonPlan = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { lesson, lessonPlan } = useLesson();
  const { aiAxios } = useFirebaseContext();

  const handleGenerateLessonPlan = () => {
    if (lessonPlan) return;
    if (!lesson) return;
    setIsGenerating(true);
    aiAxios
      .post(
        "/static-resource-generator/lesson-plans",
        {
          lessonId: lesson.id,
          subjects: lesson.subjects || [],
          duration: lesson.duration,
          lessonNote: lesson?.note,
          useStudentProfile: true,
        },
        {
          timeout: 30000,
        }
      )
      .finally(() => {
        setIsGenerating(false);
      });
  };

  const handleUpdateLessonPlan = async (
    title: string,
    overview: string,
    steps: LessonStep[]
  ) => {
    if (!lessonPlan?.id) {
      console.error("Lesson plan not found");
      return;
    }

    try {
      const lessonPlanRef = doc(db, "lessonPlans", lessonPlan.id);
      await updateDoc(lessonPlanRef, {
        ...lessonPlan,
        title,
        overview,
        steps,
      });
    } catch (error) {
      console.error("Error updating lesson plan:", error);
    }
  };

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TabHeader
        icon={<ListNumbersIcon size={16} weight="fill" />}
        title="Lesson Plan"
        handleGenerate={handleGenerateLessonPlan}
        isGenerating={isGenerating}
      />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0, // Ensures the Box can shrink properly in a flex column
        }}
      >
        {isGenerating || lessonPlan?.status === "Generating" ? (
          <LessonPlanSkeleton />
        ) : lessonPlan ? (
          <LessonPlanItem
            lessonPlan={lessonPlan}
            onUpdate={handleUpdateLessonPlan}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography variant="text15B">
              No Lesson plan found.
            </Typography>
            <Typography variant="text13L">You can generate a lesson plan using Luna AI.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LessonPlan;
